/**
 * BaseDialog Component
 * Standardized dialog wrapper with consistent loading, error, and success states
 * Reduces boilerplate in all dialog components
 *
 * Replaces repetitive dialog patterns across:
 * - DocumentDialog, FAQDialog, GroupDialog, CreateGroupDialog, EditGroupDialog
 * - ShareFolderDialog, ConnectionFormDialog, etc.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface BaseDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback when dialog should close */
  onClose: () => void;

  /** Dialog title */
  title: string;

  /** Dialog subtitle/description (optional) */
  subtitle?: string;

  /** Dialog content */
  children: React.ReactNode;

  /** Action buttons (e.g., Save, Cancel) */
  actions?: React.ReactNode;

  /** Loading state - shows spinner overlay */
  loading?: boolean;

  /** Error message to display */
  error?: string | null;

  /** Success message to display */
  success?: string | null;

  /** Maximum dialog width */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

  /** Whether dialog fills max width */
  fullWidth?: boolean;

  /** Whether dialog is full screen */
  fullScreen?: boolean;

  /** Whether to show close button in title */
  showCloseButton?: boolean;

  /** Whether clicking backdrop closes dialog (default: true) */
  disableBackdropClick?: boolean;

  /** Whether ESC key closes dialog (default: true) */
  disableEscapeKeyDown?: boolean;

  /** Custom class name */
  className?: string;

  /** Scroll behavior */
  scroll?: 'paper' | 'body';

  /** Additional props for Dialog component */
  DialogProps?: Record<string, unknown>;
}

/**
 * Base Dialog component with standardized patterns
 *
 * @example
 * ```tsx
 * const dialog = useDialog();
 *
 * return (
 *   <BaseDialog
 *     {...dialog.props}
 *     title="Create User"
 *     loading={dialog.loading}
 *     error={dialog.error}
 *     actions={
 *       <>
 *         <Button onClick={dialog.closeDialog}>Cancel</Button>
 *         <Button onClick={handleSubmit} disabled={dialog.loading}>
 *           Save
 *         </Button>
 *       </>
 *     }
 *   >
 *     <YourFormContent />
 *   </BaseDialog>
 * );
 * ```
 */
export const BaseDialog: React.FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  loading = false,
  error = null,
  success = null,
  maxWidth = 'sm',
  fullWidth = true,
  fullScreen = false,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  className,
  scroll = 'paper',
  DialogProps = {}
}) => {
  const { t } = useTranslation();

  const handleClose = (_: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Prevent closing if disabled
    if (reason === 'backdropClick' && disableBackdropClick) {return;}
    if (reason === 'escapeKeyDown' && disableEscapeKeyDown) {return;}
    // Don't allow closing while loading
    if (loading) {return;}

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      className={className}
      {...DialogProps}
    >
      {/* Title Bar */}
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={() => onClose()}
              disabled={loading}
              sx={{
                color: 'grey.500',
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      {/* Content Area */}
      <MuiDialogContent>
        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 9999
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t('common.loading')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Actual Content */}
        <Box sx={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          {children}
        </Box>
      </MuiDialogContent>

      {/* Actions */}
      {actions && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

/**
 * Convenience component for dialog content with common patterns
 */
export interface DialogContentWrapperProps {
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  /** Padding (default: 2) */
  padding?: number;
}

export const DialogContentWrapper: React.FC<DialogContentWrapperProps> = ({
  children,
  loading = false,
  error = null,
  success = null,
  padding = 2
}) => {
  return (
    <Box sx={{ p: padding }}>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

export default BaseDialog;
